/**
 * Térképkép a feed-kártyákhoz — Mapbox Static Images API.
 *
 * MIÉRT NEM A `MapView`? Mert a `MapView` minden példánya egy önálló WebGL
 * kontextust foglal, a böngészők pedig laponként nagyjából tizenhatot
 * engednek. Egy húsz kártyás feed ettől nem lassú lenne, hanem eltörne: a
 * tizenhetedik térkép egyszerűen nem jelenne meg. Ráadásul a `mapbox-gl`
 * csomag 521 kB tömörítve — a feed-kártyáért ezt betölteni aránytalan.
 *
 * A statikus kép ehelyett egyetlen `<img>`: nincs JavaScript, a böngésző
 * gyorsítótárazza, és `loading="lazy"` mellett a képernyőn kívüli kártyák
 * semmibe sem kerülnek.
 *
 * KORLÁT, amivel számolni kell: a Mapbox ingyenes kerete havi 50 000 statikus
 * kép. Húsz kártyás feednél ez ~2500 feed-betöltés havonta (a gyorsítótárból
 * kiszolgáltak nélkül). Amikor ez kevés lesz, a következő lépés a szerveren
 * generált és tárolt előnézeti kép — a specifikáció eleve ezt írja le
 * („automatikusan generált térképkép").
 */

import { decodePolyline, encodePolyline, simplifyTrace } from '@/game/polyline';
import { mapboxToken } from '@/lib/mapbox';
import type { Theme } from '@/lib/theme';

/**
 * Ennyi pont fér el biztonságosan az URL-ben.
 *
 * A kódolt nyomvonal karaktereinek nagyjából a fele URL-ben nem biztonságos,
 * és százalékos kódolással háromszorosára nő. A Mapbox 8192 karakteres
 * korlátjánál ez ~150 pontnál kezd szűkös lenni — egy 400 képpont széles
 * kártyán viszont ennél több pont már nem is látszik.
 */
const THUMBNAIL_POINTS = 150;

const STROKE = { width: 4, color: '7c3aed', opacity: 1 };

/**
 * A `mapbox://styles/user/id` alakot a statikus API `user/id` alakra várja.
 * A beépített stílusoknál ez `mapbox/light-v11`.
 */
function styleSlug(style: string): string {
  return style.replace(/^mapbox:\/\/styles\//, '');
}

function mapStyle(theme: Theme): string {
  const light = import.meta.env.VITE_MAPBOX_STYLE_LIGHT;
  const dark = import.meta.env.VITE_MAPBOX_STYLE_DARK;
  return theme === 'dark'
    ? dark || 'mapbox://styles/mapbox/dark-v11'
    : light || 'mapbox://styles/mapbox/light-v11';
}

export interface StaticMapOptions {
  width?: number;
  height?: number;
  theme: Theme;
}

/**
 * Az útvonal térképképének címe, vagy `null`, ha nincs mit mutatni.
 *
 * A `null` nem hiba: ez a normális válasz akkor is, ha nincs Mapbox-token
 * (fejlesztői környezet), és akkor is, ha az egész aktivitás a privát
 * védőkörön belül zajlott. A hívó ilyenkor a saját tartalék felületét mutatja.
 */
export function routeImageUrl(
  encodedRoute: string,
  { width = 640, height = 260, theme }: StaticMapOptions,
): string | null {
  if (!mapboxToken || encodedRoute.length === 0) return null;

  const thumbnail = thinRoute(encodedRoute);
  if (thumbnail.length === 0) return null;

  const path = `path-${STROKE.width}+${STROKE.color}-${STROKE.opacity}(${encodeURIComponent(thumbnail)})`;

  // `auto` = a Mapbox illeszti a kivágást a nyomvonalra; `@2x` a retina
  // kijelzőkhöz. A logót és az attribúciót SZÁNDÉKOSAN nem kapcsoljuk ki:
  // a Mapbox feltételei megkövetelik a feltüntetésüket.
  return (
    `https://api.mapbox.com/styles/v1/${styleSlug(mapStyle(theme))}/static/` +
    `${path}/auto/${Math.round(width)}x${Math.round(height)}@2x` +
    `?padding=24&access_token=${mapboxToken}`
  );
}

/** Egy pont helye a képen, a kép szélességének/magasságának százalékában. */
export interface ImagePoint {
  x: number;
  y: number;
}

export interface RouteImageView {
  url: string;
  /** Az útvonal első és utolsó pontja a képen — a START / FINISH tűhöz. */
  start: ImagePoint;
  finish: ImagePoint;
  /** Ugyanez földrajzi koordinátában (a közös-tű döntéshez). */
  startLatLng: { lat: number; lng: number };
  finishLatLng: { lat: number; lng: number };
}

/** A Mapbox GL (és a statikus API) csempemérete: 512 px a 0-s zoomon. */
const WORLD_PX = 512;
const PADDING_PX = 24;
/** Egy pontnyi (vagy nagyon rövid) útvonalnál se zoomoljunk az épületekig. */
const MAX_ZOOM = 16;

function mercator(lat: number, lng: number): { x: number; y: number } {
  const sin = Math.sin((lat * Math.PI) / 180);
  return {
    x: ((lng + 180) / 360) * WORLD_PX,
    y: (0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)) * WORLD_PX,
  };
}

function unmercator(x: number, y: number): { lat: number; lng: number } {
  const n = Math.PI - (2 * Math.PI * y) / WORLD_PX;
  return {
    lng: (x / WORLD_PX) * 360 - 180,
    lat: (180 / Math.PI) * Math.atan(Math.sinh(n)),
  };
}

/**
 * Mint a `routeImageUrl`, de a kivágást (középpont + zoom) MI számoljuk, nem a
 * Mapbox `auto` módja — így pontosan tudjuk, hová esik a kép bármely pontja.
 * Ebből kerül a START / FINISH tű HTML-ben a kép fölé (a statikus API saját
 * jelölőire felirat nem tehető).
 *
 * ⚠️ A képet a MEGJELENÍTETT doboz arányában kell kérni: `object-fit: cover`
 * mellett minden levágott sáv elcsúsztatná a tűket.
 */
export function routeImageView(
  encodedRoute: string,
  { width = 640, height = 280, theme }: StaticMapOptions,
): RouteImageView | null {
  if (!mapboxToken || encodedRoute.length === 0) return null;
  const route = decodePolyline(encodedRoute);
  const thumbnail = thinRoute(encodedRoute);
  if (route.length < 2 || thumbnail.length === 0) return null;

  const projected = route.map((point) => mercator(point.lat, point.lng));
  const xs = projected.map((p) => p.x);
  const ys = projected.map((p) => p.y);
  const minX = Math.min(...xs), maxX = Math.max(...xs);
  const minY = Math.min(...ys), maxY = Math.max(...ys);

  const fitX = (width - 2 * PADDING_PX) / Math.max(maxX - minX, 1e-9);
  const fitY = (height - 2 * PADDING_PX) / Math.max(maxY - minY, 1e-9);
  // Két tizedesre kerekítve kérjük — a tűket is ezzel a zoommal számoljuk.
  const zoom = Math.round(Math.min(MAX_ZOOM, Math.log2(Math.min(fitX, fitY))) * 100) / 100;
  const center = unmercator((minX + maxX) / 2, (minY + maxY) / 2);
  const lng = Math.round(center.lng * 1e6) / 1e6;
  const lat = Math.round(center.lat * 1e6) / 1e6;
  const origin = mercator(lat, lng);
  const scale = 2 ** zoom;

  const at = (point: { lat: number; lng: number }): ImagePoint => {
    const p = mercator(point.lat, point.lng);
    return {
      x: ((width / 2 + (p.x - origin.x) * scale) / width) * 100,
      y: ((height / 2 + (p.y - origin.y) * scale) / height) * 100,
    };
  };

  const path = `path-${STROKE.width}+${STROKE.color}-${STROKE.opacity}(${encodeURIComponent(thumbnail)})`;
  return {
    url:
      `https://api.mapbox.com/styles/v1/${styleSlug(mapStyle(theme))}/static/` +
      `${path}/${lng},${lat},${zoom}/${Math.round(width)}x${Math.round(height)}@2x` +
      `?access_token=${mapboxToken}`,
    start: at(route[0]!),
    finish: at(route[route.length - 1]!),
    startLatLng: { lat: route[0]!.lat, lng: route[0]!.lng },
    finishLatLng: { lat: route[route.length - 1]!.lat, lng: route[route.length - 1]!.lng },
  };
}

/** A nyomvonal ritkítása kártyaméretre — az URL hosszkorlátja miatt. */
function thinRoute(encodedRoute: string): string {
  const points = decodePolyline(encodedRoute);
  if (points.length <= THUMBNAIL_POINTS) return encodedRoute;

  let thinned = points;
  for (let epsilon = 15; thinned.length > THUMBNAIL_POINTS && epsilon <= 500; epsilon *= 2) {
    thinned = simplifyTrace(points, epsilon);
  }

  return encodePolyline(thinned);
}
